import { useEffect, useState } from 'react';
import { useCountDown } from 'ahooks';
import { useLocation } from 'react-router-dom';

import {
  BindPaymentType,
  EmailAuthCodeScopeEnum,
  LoginInterface,
  PersonalPayInfo,
  PersonalVerifyResult,
} from 'common/types';
import useAntStaticComponent from 'common/hooks/useAntStaticComponent';
import service from '@/service';
import { useUserAccount } from './useUserAccount';
import { FastApiStatusCode } from 'common/service/constants/fastApi';

const errorOpt = [
  {
    text: '持卡人信息与输入信息不一致',
    code: 2,
  },
  {
    text: '查无此银行卡',
    code: 3,
  },
  {
    text: '查无此身份证',
    code: 4,
  },
  {
    text: '手机号码格式不正确',
    code: 5,
  },
  {
    text: '银行卡号不正确',
    code: 6,
  },
  {
    text: '其他出错，请联系客服',
    code: 7,
  },
];

const DEFAULT_COUNTDOWN = 120 * 1000;

export enum BindStatus {
  Bound = 1, // 已经绑定了手机号，无需再次绑定
  NeedBind, // 未绑定手机号，需要绑定
  BindFail, // 验证码错误导致绑定失败
  PhoneExist, //手机号已经存在
}
export function useVerifyBind() {
  const { message: antMessage, modal } = useAntStaticComponent();
  const { confirm } = modal;

  const { getAccount, userAccount } = useUserAccount();
  const [loading, setLoading] = useState(false);
  const [codeLoading, setCodeLoading] = useState(false); // 验证码loading
  const [bindResult, setBindResult] = useState({
    status: userAccount?.phone ? BindStatus.Bound : BindStatus.NeedBind,
    msg: '',
  });
  const [targetDate, setTargetDate] = useState(new Date());
  const [countdown] = useCountDown({
    targetDate,
  });

  const [paymentInfo, setPaymentInfo] = useState<PersonalPayInfo>({
    alipayNo: '',
    bankCardNo: '',
  });

  //   const history = useHistory();

  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const infoType = urlParams.get('infoType');

  async function bindAliPay(alipayNo: string) {
    // setLoading(loading);
    const { code, message: msg } = await service.userSetting.bindAlipay({ alipayNo });
    // setLoading(false);
    // return get(res, 'code');
    return { verify: code === 0, msg } as PersonalVerifyResult;
  }

  const bindCard = async (params: { bankCardNo: string; phone: string; bankBranch: string }) => {
    try {
      // setLoading(loading);
      const { data, code, message: msg } = await service.userSetting.bindCard(params);

      return { verify: code === 0, msg } as PersonalVerifyResult;
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const bindOverseasCard = async (params: { bankCardNo: string; swiftCode: string; accountName: string }) => {
    try {
      // setLoading(loading);
      const { data, code, message: msg } = await service.userSetting.bindOverseasCard(params);

      return { verify: code === 0, msg } as PersonalVerifyResult;
    } catch (e) {
      return Promise.reject(e);
    }
  };

  async function unbindPayment(payload: { code: string }, infoType: BindPaymentType) {
    try {
      let result = { code: 0, data: undefined, message: '' };
      setLoading(true);

      if (infoType === BindPaymentType.ALI_PAY) {
        result = await service.userSetting.unbindAlipay(payload);
      } else {
        result = await service.userSetting.unbindBankCard(payload);
      }
      if (result?.code === 0) {
        antMessage.success('解绑成功');
      } else {
        antMessage.error(result?.message);
      }
      return result;
    } catch (e) {
      console.error(e);
      return e;
    } finally {
      setLoading(false);
    }
  }

  async function bindPhone(params: { phone: string; code: string; region: string }) {
    try {
      setLoading(true);
      const { code, message: msg } = (await service.userSetting.bindPhone(params)) as any;
      const isSuc = code === 0 || msg === 'success';
      if (isSuc) {
        antMessage.success('绑定成功');
        setBindResult({ status: BindStatus.Bound, msg: '绑定成功' });
        return true;
      } else {
        if (code === FastApiStatusCode.PhoneExist) {
          setBindResult({ status: BindStatus.PhoneExist, msg: msg || '绑定失败' });
          throw new Error(msg);
        } else {
          setBindResult({ status: BindStatus.BindFail, msg: msg || '绑定失败' });
          throw new Error(msg);
        }
      }
    } catch (e: any) {
      console.error(e);
      setBindResult({
        status: e.code === FastApiStatusCode.PhoneExist ? BindStatus.PhoneExist : BindStatus.BindFail,
        msg: (e as any)?.msg || '绑定失败',
      });

      return false;
    } finally {
      setLoading(false);
    }
  }
  //换绑邮箱
  const changeEmail = async (params: { email: string; code: string }) => {
    try {
      setLoading(true);
      const { code, message: msg } = await service.userSetting.changeEmail(params);
      const isSuc = code === 0 || msg === 'success';
      if (isSuc) {
        antMessage.success('绑定成功');
        return true;
      } else {
        antMessage.error(msg);
        throw new Error(msg);
      }
    } catch (e) {
      return false;
    } finally {
      setLoading(false);
    }
  };
  //换绑手机号
  const changePhone = async (params: { phone: string; code: string; region: string }) => {
    try {
      setLoading(true);
      const { code, message: msg } = await service.userSetting.changePhone(params);
      const isSuc = code === 0 || msg === 'success';
      if (isSuc) {
        antMessage.success('绑定成功');
        return true;
      } else {
        antMessage.error(msg);
        throw new Error(msg);
      }
    } catch (e: any) {
      antMessage.error(e?.message || '换绑手机号失败，请稍后重试');
      return false;
    } finally {
      setLoading(false);
    }
  };

  function cancel() {
    // history.goBack();
  }

  async function sendCode() {
    if (codeLoading || countdown > 0) return;
    try {
      setCodeLoading(true);
      const { code, message: msg } = await service.userSetting.sendMyCode();
      if (code !== 0) {
        antMessage.error(msg);
      } else {
        setTargetDate(new Date(Date.now() + DEFAULT_COUNTDOWN));
      }
    } catch {
    } finally {
      setCodeLoading(false);
    }
  }

  async function sendCodeToPhone(payload: LoginInterface & { scope?: number }) {
    if (codeLoading || countdown > 0) return;
    try {
      setCodeLoading(true);
      const { code, message: msg } = await service.userSetting.sendCode(payload);
      if (code !== 0) {
        antMessage.error(msg);
      } else {
        setTargetDate(new Date(Date.now() + DEFAULT_COUNTDOWN));
      }
    } catch {
    } finally {
      setCodeLoading(false);
    }
  }
  const getPaymentInfo = async () => {
    const { data } = await service.userSetting.getPaymentInfo();
    setPaymentInfo(data);
    return data;
  };

  const checkPhoneCode = async (phoneCode: string) => {
    const { code, message } = await service.userSetting.checkPhoneCode(phoneCode);
    if (code !== 0) {
      antMessage.error(message);
    }
    return code === 0;
  };
  const checkEmailCode = async (emailCode: string) => {
    const { code, message } = await service.userSetting.checkEmailCode(emailCode);
    if (code !== 0) {
      antMessage.error(message);
    }
    return code === 0;
  };

  const validateNewPhone = async (params: { phone: string; region: string }) => {
    const { code, message } = await service.userSetting.validateNewPhone(params);

    return {
      status: code === 0,
      msg: message,
    };
  };
  const validateNewEmail = async (params: { email: string }) => {
    const { code, message } = await service.userSetting.validateNewEmail(params);
    return {
      status: code === 0,
      msg: message,
    };
  };
  const sendEmailCode = async (params: { scope: EmailAuthCodeScopeEnum; email?: string }) => {
    if (codeLoading || countdown > 0) return;
    try {
      setCodeLoading(true);
      const { code, message: msg } = await service.userSetting.sendEmailCode(params);
      if (code !== 0) {
        antMessage.error(msg);
      } else {
        setTargetDate(new Date(Date.now() + DEFAULT_COUNTDOWN));
      }
    } catch {
    } finally {
      setCodeLoading(false);
    }
  };

  useEffect(() => {
    getAccount();
    getPaymentInfo();
  }, []);

  useEffect(() => {
    setBindResult({ status: userAccount?.phone ? BindStatus.Bound : BindStatus.NeedBind, msg: '' });
  }, [userAccount?.phone]);

  return {
    loading,
    bindAliPay,
    bindCard,
    bindOverseasCard,
    bindPhone,
    cancel,
    codeLoading,
    countdown: ~~(countdown / 1000),
    unbindPayment,
    sendCode,
    sendCodeToPhone,
    sendEmailCode,

    bindResult,
    getPaymentInfo,
    paymentInfo,
    checkPhoneCode,
    checkEmailCode,
    validateNewPhone,
    validateNewEmail,
    changeEmail,
    changePhone,
    setBindResult,
  };
}
